import { QueryClient } from '@tanstack/react-query'
import { GetStaticPaths, GetStaticProps } from 'next'
import BasicHead from '../../../../../components/common/BasicHead'
import CsrOnlyStudentBrazePixel from '../../../../../components/common/CsrOnlyStudentBrazePixel'
import BlogDetailPortableTextSection from '../../../../../components/landing/blog/Detail/BlogDetailPortableTextSection'
import BlogDetailResponsive from '../../../../../components/landing/blog/Detail/BlogDetailResponsive'
import { IBlogDetail, IPost } from '../../../../../components/landing/blog/type'
import { logVisitEvent } from '../../../../../modules/braze/logVisitRecord'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import { IS_DEVELOPMENT, IS_PRODUCTION, RINGLE_DOMAIN } from '../../../../../modules/envVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import client from '../../../../../modules/sanityClient'
import { buildSanityImage } from '../../../../../modules/utils'
import { FETCH_DETAIL_BLOG_DATA_QUERY, FETCH_POST_LIST_QUERY } from '../../../../../sanity/groqQueries'
import { useDetectBackNavigation } from '../../../../../store/useScrollRestoreOnBack'

// Define the BlogDetail component
const BlogDetail = ({ post, ogData, seoData }: IBlogDetail) => {
  const { locale } = useTranslation()
  const router = useAppRouter()
  const isMobile = useIsMobile()
  const { currentUser } = useCurrentUser()
  const slug = router.query.slug

  useDetectBackNavigation()

  return (
    <>
      {/*SEO og data */}
      <BasicHead seoData={seoData} ogData={ogData} />

      <CsrOnlyStudentBrazePixel
        currentUser={currentUser}
        callback={(appBoy) =>
          logVisitEvent(appBoy, 'view_blog', {
            article_name: slug,
            locale: locale,
          })
        }
      />
      <main>
        <article>
          <BlogDetailResponsive post={post} />
          {/* <div className="sm:block hidden">
            <BlogDetailDesktop post={post}>
              <BlogContent post={post} />
            </BlogDetailDesktop>
          </div>
          <div className="block sm:hidden">
            <BlogDetailMobile post={post}>
              <BlogContent post={post} />
            </BlogDetailMobile>
          </div> */}
        </article>
      </main>
    </>
  )
}

const BlogContent = ({ post }: { post: IPost }) => {
  return <BlogDetailPortableTextSection value={post.content} />
}

export const fetchDetailBlogData = async (slug, locale) => {
  try {
    const post = await client.fetch(FETCH_DETAIL_BLOG_DATA_QUERY, { slug })
    if (!post) {
      console.error(`fetchDetailBlogData: No post found for slug=${slug}`)
    }
    return post || null
  } catch (error) {
    console.error(`fetchDetailBlogData error:`, error)
    return null
  }
}

export const fetchPostList = async () => {
  try {
    const post = await client.fetch(FETCH_POST_LIST_QUERY)
    return post || []
  } catch (error) {
    console.error(`fetchPostList error:`, error)
    return []
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (!IS_PRODUCTION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const postList = await fetchPostList()
  const paths = postList.map((item) => {
    return { params: { lang: item.language, slug: item.slug } }
  })

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { slug, lang } = ctx.params
  const queryClient = new QueryClient()

  const post = await fetchDetailBlogData(slug, lang)

  if (!post) {
    console.error(`getStaticProps: No post found for slug=${slug}`)
    return { notFound: true }
  }

  await queryClient.prefetchQuery(['post', slug], () => Promise.resolve(post))

  const metaData = post?.metaData || {}

  const seoData = {
    pageTitle: metaData.metaTitle || '',
    pageDescription: metaData.metaDescription || '',
    pageKeywords: metaData.metaKeyword || '',
  }

  const ogData = {
    title: metaData.ogTitle || '',
    desc: metaData.ogDescription || '',
    image: post.thumbnail && Object.keys(post.thumbnail).length > 0 ? buildSanityImage(post.thumbnail).url() : '',
    ogUrl: `https://${IS_DEVELOPMENT ? 'qa7.ringlefront.com' : RINGLE_DOMAIN}/${
      ctx.params.lang
    }/student/landing/blog/${slug}`,
  }

  return {
    props: {
      post,
      seoData,
      ogData,
    },
    revalidate: 900,
  }
}

export default BlogDetail
